/**
 * IsometricEngine.ts
 * 2:1 Dimetric Isometric Projection, Coordinate Transformation, and Depth Sorting Engine.
 * Adheres strictly to mathematical specifications in PROJECT.md and survey_report.md.
 */

import { Pathfinding } from './Pathfinding';

export interface IsoPoint {
  isoX: number;
  isoY: number;
}

export interface GridCoord {
  col: number;
  row: number;
}

export interface IsoConfig {
  originX: number;
  originY: number;
  tileWidth: number;
  tileHeight: number;
  halfWidth: number;
  halfHeight: number;
  gridSize: number;
}

export const ISO_CONSTANTS: IsoConfig = {
  originX: 195,
  originY: 240,
  tileWidth: 40,
  tileHeight: 20,
  halfWidth: 20,
  halfHeight: 10,
  gridSize: 8,
};

export interface IIsometricEngine {
  gridToScreen(col: number, row: number, zOffset?: number): IsoPoint;
  screenToGrid(screenX: number, screenY: number): GridCoord;
  isWithinBounds(col: number, row: number): boolean;
  findPath(start: GridCoord, target: GridCoord, obstacles: Set<string>): GridCoord[];
  calculateDepth(col: number, row: number, layerPriority?: number): number;
}

export class IsometricEngine implements IIsometricEngine {
  public readonly config: IsoConfig;

  constructor(customConfig?: Partial<IsoConfig>) {
    this.config = {
      ...ISO_CONSTANTS,
      ...customConfig,
    };
  }

  /**
   * Forward Projection: Converts Cartesian grid coordinates (col, row) to Screen (isoX, isoY).
   * Formula:
   *   isoX = originX + (col - row) * halfWidth
   *   isoY = originY + (col + row) * halfHeight - zOffset
   */
  public gridToScreen(col: number, row: number, zOffset: number = 0): IsoPoint {
    const isoX = this.config.originX + (col - row) * this.config.halfWidth;
    const isoY = this.config.originY + (col + row) * this.config.halfHeight - zOffset;
    return { isoX, isoY };
  }

  /**
   * Inverse Projection: Converts Screen touch point (screenX, screenY) to continuous Grid coordinates (col, row).
   * Formula:
   *   dx = screenX - originX
   *   dy = screenY - originY
   *   col = (dy / halfHeight + dx / halfWidth) / 2
   *   row = (dy / halfHeight - dx / halfWidth) / 2
   */
  public screenToGrid(screenX: number, screenY: number): GridCoord {
    const dx = screenX - this.config.originX;
    const dy = screenY - this.config.originY;
    const col = (dy / this.config.halfHeight + dx / this.config.halfWidth) / 2;
    const row = (dy / this.config.halfHeight - dx / this.config.halfWidth) / 2;
    return { col, row };
  }

  /**
   * Clamps continuous coordinates to discrete integer grid coordinates [0, gridSize - 1].
   */
  public clampGrid(val: number): number {
    return Math.max(0, Math.min(this.config.gridSize - 1, Math.round(val)));
  }

  /**
   * Validates whether (col, row) lies strictly within the playable room diamond.
   */
  public isWithinBounds(col: number, row: number): boolean {
    return col >= 0 && col < this.config.gridSize && row >= 0 && row < this.config.gridSize;
  }

  /**
   * Dynamic Painter's Y-Sorting Depth calculation.
   * Entities closer to the viewer (higher col + row) render in front of entities further back.
   * Formula:
   *   depth = (col + row) * 100 + layerPriority
   */
  public calculateDepth(col: number, row: number, layerPriority: number = 0): number {
    return (col + row) * 100 + layerPriority;
  }

  /**
   * Delegates pathfinding to Pathfinding A* engine.
   */
  public findPath(start: GridCoord, target: GridCoord, obstacles: Set<string>): GridCoord[] {
    return Pathfinding.findPath(start, target, obstacles, this.config.gridSize);
  }

  // Static convenience wrappers
  public static gridToScreen(col: number, row: number, zOffset: number = 0): IsoPoint {
    const isoX = ISO_CONSTANTS.originX + (col - row) * ISO_CONSTANTS.halfWidth;
    const isoY = ISO_CONSTANTS.originY + (col + row) * ISO_CONSTANTS.halfHeight - zOffset;
    return { isoX, isoY };
  }

  public static screenToGrid(screenX: number, screenY: number): GridCoord {
    const dx = screenX - ISO_CONSTANTS.originX;
    const dy = screenY - ISO_CONSTANTS.originY;
    const col = (dy / ISO_CONSTANTS.halfHeight + dx / ISO_CONSTANTS.halfWidth) / 2;
    const row = (dy / ISO_CONSTANTS.halfHeight - dx / ISO_CONSTANTS.halfWidth) / 2;
    return { col, row };
  }

  public static clampGrid(val: number, max: number = ISO_CONSTANTS.gridSize): number {
    return Math.max(0, Math.min(max - 1, Math.round(val)));
  }

  public static isWithinBounds(col: number, row: number, max: number = ISO_CONSTANTS.gridSize): boolean {
    return col >= 0 && col < max && row >= 0 && row < max;
  }

  public static calculateDepth(col: number, row: number, layerPriority: number = 0): number {
    return (col + row) * 100 + layerPriority;
  }
}

export const defaultIsometricEngine = new IsometricEngine();
